import type { RiskGapModel } from '@ctw/shared/api/fhir/models/risk-gap';
import { Badge } from '@ctw/shared/components/badge';
import type { PatientRiskGapsTableColumn } from '@ctw/shared/content/patient-risk-gaps/components/resource-table';
import { confidenceLevelToRating } from '@ctw/shared/content/risk/risk-gap-confidence-level';
import { RiskGapInfoIcon, infoForRiskGapIcon } from '@ctw/shared/content/risk/risk-gap-info-icon';
import { riskGapStatusBadgeDefinitionV2 } from '@ctw/shared/content/risk/risk-gap-status-badge-definition-v2';
import { riskGapTypeBadgeDefinition } from '@ctw/shared/content/risk/risk-gap-type-badge-definition';
import { formatFHIRDate } from '@ctw/shared/utils/dates';
import { tw } from '@ctw/shared/utils/tailwind';
import { RISK_GAP_VALUES } from '@zeus-health/types';
import { startCase } from 'lodash-es';

export const buildPatientRiskGapsColumnsV2 = (): Array<
  PatientRiskGapsTableColumn<RiskGapModel>
> => [
  {
    title: 'HCC',
    render: (model, hasSelectedRow) => {
      const title = hasSelectedRow
        ? model.title.length > 86
          ? `${model.title.slice(0, 86)}…`
          : model.title
        : model.title;

      return (
        <div className={tw`flex flex-row items-center justify-between`}>
          <div className={tw`flex flex-col font-semibold text-sm`}>
            {title} {`(HCC ${model.hccCode})`}
          </div>
        </div>
      );
    },
    minimizedClassName: `${tw`order-3 w-full p-0 mb-2`}`,
    className: `${tw`order-3 md:order-1 p-0 md:p-4 mb-2 md:mb-0 max-w-auto md:max-w-[600px] hover:underline`}`,
  },
  {
    title: 'Type',
    render: (model) => {
      return (
        <Badge
          size={'sm'}
          {...riskGapTypeBadgeDefinition(model.type)}
          content={startCase(model.type)}
        />
      );
    },
    minimizedClassName: `${tw`order-1 w-fit p-0 mb-2`}`,
    className: `${tw`order-1 md:order-2 w-fit p-0 md:p-4 mb-2 md:mb-0`}`,
  },
  {
    title: 'Status',
    render: (model) => (
      <Badge
        size={'sm'}
        {...riskGapStatusBadgeDefinitionV2(model)}
        icon={undefined}
        className={tw`rounded-full border bg-white text-content-main`}
      />
    ),
    minimizedClassName: `${tw`order-2 w-fit p-0 mb-2`}`,
    className: `${tw`order-2 md:order-3 w-fit p-0 md:p-4 mb-2 md:mb-0`}`,
  },
  {
    title: 'Confidence',
    render: (model) => {
      const level =
        model.type === 'net-new' ? 'high' : confidenceLevelToRating(model.confidenceLevel);
      const display = model.type !== 'recapture' ? startCase(level) : '—';
      return (
        <div
          className={tw`hidden flex-col overflow-hidden text-ellipsis whitespace-nowrap text-sm md:flex `}
        >
          {display}
        </div>
      );
    },
    minimizedClassName: `${tw`hidden`}`,
    className: `${tw` w-fit p-0 md:p-4 mb-2 md:mb-0`}`,
  },
  {
    title: 'Last Updated',
    render: (model, shouldMinimize) => {
      const hideInfoIcon =
        !shouldMinimize ||
        model.status !==
          RISK_GAP_VALUES.MEASURE_REPORT.EXTENSION.RISK_GAP_STATUS_UPDATE
            .EXPECTED_TO_CLOSE_FIRST_PARTY;
      return (
        <div
          className={tw`flex overflow-hidden text-ellipsis whitespace-nowrap text-content-subtle text-xs`}
        >
          <RiskGapInfoIcon
            hide={hideInfoIcon}
            info={infoForRiskGapIcon(model.status)}
            className={tw`mr-2 pb-1`}
          />
          {formatFHIRDate(model.lastStatusUpdateDate)} by {model.lastUpdatedByDisplay}
        </div>
      );
    },
    minimizedClassName: `${tw`order-4 w-fit p-0`}`,
    className: `${tw`order-4 w-fit p-0 md:p-4 md:mb-0`}`,
  },
];
